import { memo } from 'react'

import { EnglishPage } from '@/pages/english'

import { AudioBookPlayer } from './audio/audio-player'
import { EpubReader } from './epub/epub-reader'
import { PdfReader } from './pdf/pdf-reader'
import { VideoPlayer } from './video/video-player'
import { ZipReader } from './zip/zip-reader'

type ReaderSelectorProps = {
  mimeType: string
  baseUrl: string
  fileName: string
  title: string
  images: string[]
  onClose: () => void
  onInfoClick: () => void
  onNext?: () => void
  onPrev?: () => void
  nextEpisodeId?: string
  nextEpisodeTitle?: string
  numPages?: number
  domain?: string
  productId?: string
  episodeId?: string
  episodeIndex?: number // エピソード番号（ヘッダー表示用）
  // お気に入り・削除機能
  productIdNum?: number
  isFavorite?: boolean
  onFavoriteToggle?: () => void
  onDelete?: () => void
}

export const ReaderSelector = memo(
  ({
    mimeType,
    baseUrl,
    fileName,
    title,
    images: _images,
    onClose,
    onInfoClick,
    onNext,
    onPrev,
    nextEpisodeId,
    nextEpisodeTitle,
    numPages,
    domain,
    productId,
    episodeId,
    episodeIndex,
    productIdNum,
    isFavorite,
    onFavoriteToggle,
    onDelete,
  }: ReaderSelectorProps) => {
    const streamUrl = `${baseUrl}/stream`
    const parsedEpisodeId = episodeId ? parseInt(episodeId, 10) : undefined
    const parsedEpisodeIdOrZero = parsedEpisodeId ?? 0

    if (domain === 'english' && productId && episodeId) {
      return (
        <EnglishPage
          onClose={onClose}
          baseUrl={baseUrl}
          fileName={fileName}
          title={title}
          domain={domain}
          productId={productId}
          episodeId={episodeId}
        />
      )
    }

    if (domain === 'audiobook') {
      return (
        <AudioBookPlayer
          url={streamUrl}
          title={title}
          onClose={onClose}
          onInfoClick={onInfoClick}
          episodeId={episodeId}
          productIdNum={productIdNum}
        />
      )
    }

    switch (mimeType) {
      case 'application/epub+zip':
        return (
          <EpubReader
            title={title}
            episodeId={parsedEpisodeIdOrZero}
            numPages={numPages ?? 0}
            onClose={onClose}
            onInfoClick={onInfoClick}
          />
        )

      case 'application/pdf':
        return (
          <PdfReader
            key={parsedEpisodeId}
            url={streamUrl}
            title={title}
            episodeId={parsedEpisodeIdOrZero}
            episodeIndex={episodeIndex}
            numPages={numPages ?? 0}
            onClose={onClose}
            onInfoClick={onInfoClick}
            onNext={onNext}
            onPrev={onPrev}
            productId={productIdNum}
            isFavorite={isFavorite}
            onFavoriteToggle={onFavoriteToggle}
            onDelete={onDelete}
          />
        )

      case 'video/mp4':
        return (
          <VideoPlayer
            key={parsedEpisodeId}
            url={streamUrl}
            title={title}
            onClose={onClose}
            onInfoClick={onInfoClick}
            onNext={onNext}
            onPrev={onPrev}
            nextEpisodeId={nextEpisodeId}
            nextEpisodeTitle={nextEpisodeTitle}
            episodeId={parsedEpisodeId}
            productId={productIdNum}
          />
        )
      case 'application/zip':
      case 'application/octet-stream':
      case 'application/vnd.comicbook+zip':
        return (
          <ZipReader
            url={streamUrl}
            title={title}
            onInfoClick={onInfoClick}
            onNext={onNext}
            onPrev={onPrev}
            onClose={onClose}
            numPages={numPages}
            episodeId={parsedEpisodeId}
            episodeIndex={episodeIndex}
            productId={productIdNum}
            isFavorite={isFavorite}
            onFavoriteToggle={onFavoriteToggle}
            onDelete={onDelete}
          />
        )
      default:
        // 音楽（apple_music）は /music 配下の専用プレイヤーが担当する
        return <div>default: {mimeType} is not supported.</div>
    }
  },
)

ReaderSelector.displayName = 'ReaderSelector'
